import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

export interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const PWA_INSTALL_DISMISSED_KEY = "pwa-install-dismissed";

@Injectable({
  providedIn: "root",
})
export class PwaInstallService {
  private installPrompt = new BehaviorSubject<BeforeInstallPromptEvent | null>(null);
  private deferredPrompt: BeforeInstallPromptEvent | null = null;

  readonly canInstall$: Observable<BeforeInstallPromptEvent | null> = this.installPrompt.asObservable();

  constructor() {
    if (typeof window !== "undefined") {
      window.addEventListener("beforeinstallprompt", (e: Event) => {
        e.preventDefault();
        this.deferredPrompt = e as BeforeInstallPromptEvent;
        this.installPrompt.next(this.deferredPrompt);
      });
    }
  }

  get canShowBanner(): boolean {
    if (this.isDesktop()) return false;
    if (this.wasDismissed()) return false;
    if (this.isStandalone()) return false;
    return this.deferredPrompt !== null;
  }

  isDesktop(): boolean {
    if (typeof window === "undefined") return true;
    const ua = window.navigator.userAgent;
    return !/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile/i.test(ua);
  }

  isIos(): boolean {
    if (typeof window === "undefined") return false;
    const ua = window.navigator.userAgent;
    const platform = (window.navigator as Navigator & { platform?: string }).platform;
    return /iPad|iPhone|iPod/.test(ua) || (platform === "MacIntel" && navigator.maxTouchPoints > 1);
  }

  get canShowIosBanner(): boolean {
    if (this.isDesktop()) return false;
    if (!this.isIos()) return false;
    if (this.wasDismissed()) return false;
    if (this.isStandalone()) return false;
    return true;
  }

  isStandalone(): boolean {
    if (typeof window === "undefined") return true;
    const nav = window.navigator as Navigator & { standalone?: boolean };
    return (
      window.matchMedia("(display-mode: standalone)").matches ||
      (nav.standalone === true) ||
      (document.referrer.includes("android-app://") === true)
    );
  }

  wasDismissed(): boolean {
    if (typeof window === "undefined") return true;
    return localStorage.getItem(PWA_INSTALL_DISMISSED_KEY) === "1";
  }

  /**
   * Suppress install banner on public pay links and on client payment / receipt views
   * (direct pay, checkout, payment details). List view `/main/payments` is not suppressed.
   */
  isBannerSuppressedForUrl(url: string): boolean {
    if (typeof url !== "string" || !url) return false;
    const path = url.split("?")[0].split("#")[0];
    if (path === "/pay" || path.startsWith("/pay/")) return true;
    if (path.startsWith("/main/payments/")) return true;
    return false;
  }

  dismissBanner(): void {
    if (typeof window !== "undefined") {
      localStorage.setItem(PWA_INSTALL_DISMISSED_KEY, "1");
    }
    this.installPrompt.next(null);
  }

  async promptInstall(): Promise<boolean> {
    if (!this.deferredPrompt) return false;
    try {
      await this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      if (outcome === "accepted") this.installPrompt.next(null);
      return outcome === "accepted";
    } catch {
      return false;
    }
  }
}
